import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Lock, XCircle } from 'lucide-react';
import { problemsApi } from '@/api/endpoints';
import { getApiErrorMessage } from '@/api/client';
import type { EditorialResponse } from '@/api/types';
import { MarkdownLite } from '@/components/ui/MarkdownLite';
import { useAuthStore } from '@/store/auth';

interface Props {
  slug: string;
  /** Bepul tahlil matni masala detalidan (prerender HTML da ham) — bo'lsa so'rov yuborilmaydi */
  initialContent?: string | null;
}

const DIALECT_LABEL: Record<string, string> = {
  postgresql: 'PostgreSQL',
  postgres: 'PostgreSQL',
  mysql: 'MySQL',
  sqlite: 'SQLite',
  mssql: 'SQL Server',
  oracle: 'Oracle',
};

/** ```sql dialect=postgresql verdict=correct → {lang, dialect, verdict} */
function parseInfo(info: string) {
  const [lang = '', ...rest] = info.split(/\s+/);
  const attrs: Record<string, string> = {};
  for (const token of rest) {
    const eq = token.indexOf('=');
    if (eq > 0) attrs[token.slice(0, eq).toLowerCase()] = token.slice(eq + 1).toLowerCase();
  }
  return { lang: lang.toLowerCase(), dialect: attrs.dialect, verdict: attrs.verdict };
}

/**
 * "Yechim tahlili". Bepul tahlil matni masala detali bilan keladi va prerender HTML da bor (SEO).
 * Premium tahlil faqat tab ochilganda so'raladi — obuna bo'lmasa server matnni qaytarmaydi (locked).
 */
export function EditorialTab({ slug, initialContent }: Props) {
  if (initialContent) return <EditorialContent contentMd={initialContent} />;
  return <FetchedEditorial slug={slug} />;
}

function FetchedEditorial({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [data, setData] = useState<EditorialResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Kirish/obuna o'zgarsa qayta so'raymiz — locked holati foydalanuvchiga bog'liq
  const viewerKey = `${user?.id ?? ''}|${user?.isPremium ?? ''}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    problemsApi
      .editorial(slug)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err, t('problem.editorialError')));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, viewerKey, t]);

  if (loading) {
    return <div className="py-12 text-center text-content-secondary">{t('common.loading')}</div>;
  }
  if (error || !data) {
    return <div className="py-12 text-center text-content-secondary">{error ?? t('problem.editorialError')}</div>;
  }

  if (data.locked || !data.contentMd) {
    return (
      <div className="rounded-lg border border-[var(--medium)]/30 bg-[var(--medium)]/10 p-6 text-center">
        <Lock className="mx-auto mb-3 h-10 w-10 text-[var(--medium)]" />
        <h2 className="mb-2 text-lg font-semibold">{t('problem.editorialLockedTitle')}</h2>
        <p className="mb-4 text-sm text-content-secondary">{t('problem.editorialLocked')}</p>
        <Link
          to={isAuthenticated ? '/pricing' : '/login'}
          className="btn btn-primary inline-flex gap-2 px-5 py-2.5 text-sm font-semibold"
        >
          {isAuthenticated ? t('problem.premiumCta') : t('problem.editorialLoginCta')}
        </Link>
      </div>
    );
  }

  return <EditorialContent contentMd={data.contentMd} />;
}

/** Yechim tahlili va "O'rganish" postlari: markdown + `verdict=correct|wrong` SQL blok belgisi */
export function EditorialContent({ contentMd }: { contentMd: string }) {
  const { t } = useTranslation();

  const renderCodeHeader = (info: string) => {
    const { lang, dialect, verdict } = parseInfo(info);
    if (!verdict || (lang !== 'sql' && !DIALECT_LABEL[lang])) return null;
    const correct = verdict === 'correct';
    const Icon = correct ? CheckCircle2 : XCircle;
    return (
      <div
        className={
          'flex items-center justify-between gap-2 rounded-t-lg border border-line px-3 py-1.5 text-xs font-semibold ' +
          (correct ? 'bg-[var(--easy)]/10 text-[var(--easy)]' : 'bg-[var(--hard)]/10 text-[var(--hard)]')
        }
      >
        <span className="inline-flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" />
          {correct ? t('problem.editorialCorrect') : t('problem.editorialWrong')}
        </span>
        {dialect && <span className="font-mono font-medium opacity-80">{DIALECT_LABEL[dialect] ?? dialect}</span>}
      </div>
    );
  };

  return (
    <article>
      <MarkdownLite text={contentMd} renderCodeHeader={renderCodeHeader} />
    </article>
  );
}
